import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import Pagination from '@/components/ui/Pagination';
import TruncatedText from '@/components/ui/TruncatedText';
import TransactionDetailModal from './TransactionDetailModal';
import StatusModal from '@/components/ui/StatusModal';
import Axios from '@/shared/api/axios';
import { useSelector } from 'react-redux';
import { formatDateTime } from '@/shared/utils/formatDate';

const TransactionTable = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const detailModalRef = useRef();
  const statusModalRef = useRef(null);
  const [currentStatusCode, setCurrentStatusCode] = useState('');
  const [transactions, setTransactions] = useState([]);
  // 載入失敗要跟「真的沒有交易紀錄」分開：兩者都是空列表，但只有前者需要重試出口
  const [isLoadFailed, setLoadFailed] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10; // 每頁 10 筆

  // 取得當前操作的偶像 ID
  const currentIdolId = useSelector(state => state.user.idolId);

  // 類型選項
  const typeOptions = [
    { value: 'deposit', label: t('type_deposit') },
    { value: 'withdraw', label: t('type_withdraw') },
    { value: 'income', label: t('type_income') },
    { value: 'expenses', label: t('type_expense') }
  ];

  // 狀態選項
  const statusOptions = [
    { value: 'pending', label: t('status_pending') },
    { value: 'completed', label: t('status_completed') },
    { value: 'expired', label: t('status_expired') },
    { value: 'rejected', label: t('status_rejected') }
  ];

  // 取得交易紀錄
  const fetchTransactions = async (page = 1) => {
    if (!currentIdolId) return;

    setLoadFailed(false);

    try {
      const params = {
        page,
        limit
      };

      // 類型篩選：直接傳遞 array
      if (selectedTypes.length > 0) {
        params.types = selectedTypes.map(t => t.value);
      }

      // 狀態篩選：直接傳遞 array
      if (selectedStatuses.length > 0) {
        params.statuses = selectedStatuses.map(s => s.value);
      }

      const response = await Axios.get('/wallet/transactions', { params });

      if (response.data?.status_code === 'SYSTEM_1000') {
        // 明確擋下畸形的 payload：直接塞進 state 會讓 render 讀 undefined.length 而崩掉，
        // 全站沒有 ErrorBoundary，那是整站白畫面；丟出去則收斂成這個元件的錯誤提示
        if (!Array.isArray(response.data.data?.data)) {
          throw new Error('Unexpected transactions payload');
        }
        setTransactions(response.data.data.data);
        setTotal(response.data.data.total);
        // 頁碼只在成功後才前進，否則換頁失敗會出現分頁停在新頁、列表還是舊頁的錯亂畫面
        setCurrentPage(page);
      } else if (response.data?.status_code) {
        setLoadFailed(true);
        setCurrentStatusCode(response.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // 失敗時刻意不清空既有列表，並且獨立記錄失敗狀態：
      // 空列表在畫面上等同「沒有交易紀錄」，使用者會誤以為自己真的沒有紀錄
      setLoadFailed(true);
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Failed to fetch transactions:', error);
      }
    }
  };

  useEffect(() => {
    if (currentIdolId) {
      fetchTransactions(1); // 初始載入和篩選條件改變時重置到第一頁
    }
  }, [selectedTypes, selectedStatuses, currentIdolId]);

  // 暴露 refresh 方法給父組件
  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchTransactions(currentPage);
    }
  }));

  const handlePageChange = (page) => {
    fetchTransactions(page);
  };

  const handleViewDetail = (transaction) => {
    detailModalRef.current?.show(transaction);
  };

  // 取得類型 badge 樣式
  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'deposit':
        return 'bg-warning text-white';
      case 'withdraw':
        return 'bg-success text-white';
      case 'income':
        return 'bg-blue text-white';
      case 'expenses':
        return 'bg-orange text-white';
      default:
        return 'bg-secondary text-white';
    }
  };

  // 取得狀態 badge 樣式
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending':
        return 'text-primary border border-primary';
      case 'completed':
        return 'text-gray border border-gray';
      case 'expired':
        return 'text-danger border border-danger';
      case 'rejected':
        return 'text-danger border border-danger';
      default:
        return 'text-gray border border-gray';
    }
  };

  // 取得類型文字
  const getTypeText = (type) => {
    switch (type) {
      case 'deposit':
        return t('type_deposit');
      case 'withdraw':
        return t('type_withdraw');
      case 'income':
        return t('type_income');
      case 'expenses':
        return t('type_expense');
      default:
        return type;
    }
  };

  // 取得狀態文字
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return t('status_pending');
      case 'completed':
        return t('status_completed');
      case 'expired':
        return t('status_expired');
      case 'rejected':
        return t('status_rejected');
      default:
        return status;
    }
  };

  // 取得類型樣式（供 multiValue 使用）
  const getTypeStyles = (type) => {
    switch (type) {
      case 'deposit':
        return { bgColor: '#ffc107', textColor: 'white', border: 'none' }; // warning
      case 'withdraw':
        return { bgColor: '#198754', textColor: 'white', border: 'none' }; // success
      case 'income':
        return { bgColor: '#0d6efd', textColor: 'white', border: 'none' }; // primary
      case 'expenses':
        return { bgColor: '#fd7e14', textColor: 'white', border: 'none' }; // orange
      default:
        return { bgColor: '#6c757d', textColor: 'white', border: 'none' }; // secondary
    }
  };

  // 取得狀態樣式（供 multiValue 使用）
  const getStatusStyles = (status) => {
    switch (status) {
      case 'pending':
        return { bgColor: 'transparent', textColor: '#ff7bac', border: '1px solid #ff7bac' }; // primary
      case 'completed':
        return { bgColor: 'transparent', textColor: '#6c757d', border: '1px solid #6c757d' }; // secondary
      case 'expired':
        return { bgColor: 'transparent', textColor: '#dc3545', border: '1px solid #dc3545' }; // danger
      case 'rejected':
        return { bgColor: 'transparent', textColor: '#dc3545', border: '1px solid #dc3545' }; // danger
      default:
        return { bgColor: 'transparent', textColor: '#6c757d', border: '1px solid #6c757d' };
    }
  };

  // 自訂 Select 的 multiValue 樣式
  const customSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '38px',
      borderColor: '#dee2e6',
    }),
    multiValue: (base, state) => {
      const option = state.data;
      let styles = { bgColor: '#6c757d', textColor: 'white', border: 'none' };

      // 根據選項判斷是類型還是狀態
      if (typeOptions.find(t => t.value === option.value)) {
        styles = getTypeStyles(option.value);
      } else if (statusOptions.find(s => s.value === option.value)) {
        styles = getStatusStyles(option.value);
      }

      return {
        ...base,
        backgroundColor: styles.bgColor,
        border: styles.border,
        borderRadius: '20px',
        fontWeight: '400',
        fontSize: '14px',
        padding: '4px 4px',
      };
    },
    multiValueLabel: (base, state) => {
      const option = state.data;
      let styles = { textColor: 'white' };

      if (typeOptions.find(t => t.value === option.value)) {
        styles = getTypeStyles(option.value);
      } else if (statusOptions.find(s => s.value === option.value)) {
        styles = getStatusStyles(option.value);
      }

      return {
        ...base,
        color: styles.textColor,
        fontWeight: '400',
        padding: '0',
      };
    },
    multiValueRemove: (base, state) => {
      const option = state.data;
      let styles = { textColor: 'white' };

      if (typeOptions.find(t => t.value === option.value)) {
        styles = getTypeStyles(option.value);
      } else if (statusOptions.find(s => s.value === option.value)) {
        styles = getStatusStyles(option.value);
      }

      return {
        ...base,
        color: styles.textColor,
        ':hover': {
          backgroundColor: 'transparent',
          color: styles.textColor,
        },
      };
    },
  };

  return (
    <div id="wallet-transactions" className="bg-white rounded p-4">
      <h4 className="mb-4">{t('transaction_history')}</h4>

      {/* 篩選器 */}
      <div className="mb-4 d-flex gap-3 flex-wrap">
        <div className="wallet-select-wrapper">
          <Select
            isMulti
            options={typeOptions}
            value={selectedTypes}
            onChange={setSelectedTypes}
            placeholder={t('transaction_type')}
            styles={customSelectStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>
        <div className="wallet-select-wrapper">
          <Select
            isMulti
            options={statusOptions}
            value={selectedStatuses}
            onChange={setSelectedStatuses}
            placeholder={t('transaction_status')}
            styles={customSelectStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>
      </div>

      {/* 表格 */}
      {transactions.length > 0 ? (
        <>
          <div className="mb-4">
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr className="table-light">
                    <th scope="col" className="text-nowrap p-3">{t('transaction_time')}</th>
                    <th scope="col" className="text-nowrap p-3">{t('transaction_type')}</th>
                    <th scope="col" className="text-nowrap p-3">{t('transaction_amount')}</th>
                    <th scope="col" className="text-nowrap p-3">{t('transaction_balance')}</th>
                    <th scope="col" className="text-nowrap p-3">{t('transaction_status')}</th>
                    <th scope="col" className="text-nowrap p-3">{t('transaction_summary')}</th>
                    <th scope="col" className="text-nowrap p-3">{t('transaction_details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => (
                    <tr key={index}>
                      <td className="p-3 text-nowrap">{formatDateTime(transaction.created_at)}</td>
                      <td className="p-3">
                        <span className={`badge ${getTypeBadgeClass(transaction.type)} rounded-pill`}>
                          {getTypeText(transaction.type)}
                        </span>
                      </td>
                      <td className="p-3 text-nowrap">{transaction.amount}</td>
                      <td className="p-3 text-nowrap">{transaction.balance_after}</td>
                      <td className="p-3">
                        <span className={`badge ${getStatusBadgeClass(transaction.status)} rounded-pill`}>
                          {getStatusText(transaction.status)}
                        </span>
                      </td>
                      <td className="p-3">
                        <TruncatedText text={transaction.description} maxWidth={250} />
                      </td>
                      <td className="p-3">
                        {transaction.detail ? (
                          <button
                            className="btn btn-link p-0 text-dark"
                            onClick={() => handleViewDetail(transaction)}
                            style={{
                              transition: 'color 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#e91e63'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#212529'}
                          >
                            <FontAwesomeIcon icon={["fas", "file-lines"]} />
                          </button>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : isLoadFailed ? (
        <div className="text-center py-5">
          <p className="mb-3">{t('load_failed', { type: t('transaction_history') })}</p>
          <button className="btn btn-primaryF rounded-pill" onClick={() => fetchTransactions(currentPage)}>
            {t('retry_load')}
          </button>
        </div>
      ) : (
        <div className="text-center py-5">
          <p>{t('no_data')}</p>
        </div>
      )}

      {/* 分頁 */}
      {total > limit && (
        <Pagination
          total={total}
          limit={limit}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          scrollMode="container"
          scrollContainerSelector="#wallet-transactions"
        />
      )}

      <TransactionDetailModal ref={detailModalRef} />

      {/* API 非成功狀態碼統一由 StatusModal 顯示 */}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </div>
  );
});

TransactionTable.displayName = 'TransactionTable';

export default TransactionTable;
